// The TEAM CONTACT-DETAIL section (BUILD-DEC-127) — the addresses Ritona knows for a person, edited
// DIRECTLY. Paul's 2026-08-01 ruling removed the propose→resolve ceremony for anyone holding the
// commit key: "Add address" and "Retire" call the owning `identity` legs (IFC-IDENT-004/005), which
// are credential-enforced and refuse the agent outright (G-001 — it proposes, it never commits).
// Retire asks once first, because it stops Ritona trusting that address in BOTH directions.
//
// The section renders at all only when the projection carried this person's addresses (the
// IFC-IDENT-018 scoped verdict said yes); the edit controls render only for a key-holder — and the
// owning module re-checks every write regardless, so hiding is never the control (G-180). Tokens
// only (G-181).

import { useState } from 'react';
import { Btn } from '../primitives.tsx';
import type { ContactRow, UserRow } from '../know-projections.ts';

/** The retire consequence + the two ways out. Stated BEFORE the commit, because retiring stops
 *  Ritona trusting that address in both directions (and may leave the person unreachable). */
function RetireConfirm({
  address,
  lastLive,
  onKeep,
  onConfirm,
}: {
  readonly address: string;
  readonly lastLive: boolean;
  readonly onKeep: () => void;
  readonly onConfirm: () => void;
}): React.JSX.Element {
  return (
    <div className="tm-contact-confirm">
      <span className="tm-contact-confirm-text">
        Retire this address? Mail from it will no longer be trusted, in or out.
        {lastLive ? ' It is their only working address.' : ''}
      </span>
      <Btn kind="ghost" sm ariaLabel="Keep the address" onClick={onKeep}>
        Keep it
      </Btn>
      <Btn kind="primary" sm ariaLabel={`Confirm retiring ${address}`} onClick={onConfirm}>
        Retire
      </Btn>
    </div>
  );
}

/** The address's state, in the reader's terms: what Ritona will and won't accept from it. Since
 *  BUILD-DEC-159 a newly added address is Confirmed immediately, so "Awaiting confirmation" now marks
 *  only rows written before that ruling — which is exactly why it must keep rendering: those addresses
 *  really are still unusable in both directions until someone re-adds them. */
function StateChip({ contact }: { readonly contact: ContactRow }): React.JSX.Element {
  const retired = contact.status === 'retired';
  return (
    <span className={`tm-contact-chip ${retired ? 'retired' : contact.confirmed ? 'ok' : 'wait'}`}>
      {retired ? 'Retired' : contact.confirmed ? 'Confirmed' : 'Awaiting confirmation'}
    </span>
  );
}

/** One address row: the address, its state chip, and (for a key-holder) the retire affordance. */
function ContactLine({
  contact,
  canEdit,
  confirming,
  lastLive,
  onAskRetire,
  onKeep,
  onConfirmRetire,
}: {
  readonly contact: ContactRow;
  readonly canEdit: boolean;
  readonly confirming: boolean;
  /** True when retiring this would leave the person with no working address — stated, not blocked. */
  readonly lastLive: boolean;
  readonly onAskRetire: () => void;
  readonly onKeep: () => void;
  readonly onConfirmRetire: () => void;
}): React.JSX.Element {
  const retired = contact.status === 'retired';
  return (
    <div className="tm-contact">
      <div className="tm-contact-row">
        <span className={retired ? 'tm-contact-addr retired' : 'tm-contact-addr'}>
          {contact.address}
        </span>
        <StateChip contact={contact} />
        {canEdit && !retired && !confirming ? (
          <button
            type="button"
            className="tm-contact-retire"
            data-retire-address={contact.channelIdentityId}
            aria-label={`Retire ${contact.address}`}
            onClick={onAskRetire}
          >
            Retire
          </button>
        ) : null}
      </div>
      {confirming ? (
        <RetireConfirm
          address={contact.address}
          lastLive={lastLive}
          onKeep={onKeep}
          onConfirm={onConfirmRetire}
        />
      ) : null}
    </div>
  );
}

/** The add-an-address row — commits directly, and the address is usable at once: since BUILD-DEC-159
 *  a directory administrator adding an address IS the confirmation (there has never been a step where
 *  the person confirms from that inbox, and the copy here used to promise one). The hint states the
 *  responsibility that comes with it, because a typo becomes a real, sendable-to address. */
function ContactAdd({
  user,
  onAdd,
}: {
  readonly user: UserRow;
  readonly onAdd: (address: string) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState('');
  const submit = (): void => {
    if (draft.trim().length === 0) return;
    onAdd(draft.trim());
    setDraft('');
  };
  return (
    <div className="tm-contact-add">
      <input
        type="text"
        value={draft}
        aria-label={`Add an email address for ${user.displayName}`}
        placeholder="name@company.co.uk"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') submit();
        }}
      />
      <Btn
        kind="ghost"
        sm
        disabled={draft.trim().length === 0}
        ariaLabel="Add the address"
        onClick={submit}
      >
        Add address
      </Btn>
      <span className="tm-sec-hint">Ritona uses it straight away — check it is theirs</span>
    </div>
  );
}

/** The section: the person's addresses + (for a key-holder) the direct add/retire affordances. */
export function ContactSection({
  user,
  contacts,
  canEdit,
  onAdd,
  onRetire,
}: {
  readonly user: UserRow;
  readonly contacts: readonly ContactRow[];
  readonly canEdit: boolean;
  readonly onAdd: (address: string) => void;
  readonly onRetire: (channelIdentityId: string) => void;
}): React.JSX.Element {
  const [confirming, setConfirming] = useState<string | null>(null);
  const lastLive = contacts.filter((contact) => contact.status === 'active').length <= 1;
  return (
    <div className="tm-sec">
      <div className="tm-sec-head">
        <span className="mono-label">Email addresses</span>
        <span className="tm-sec-hint">Ritona only trusts mail from a confirmed address</span>
      </div>
      <div className="tm-contacts" data-contact-list={user.id}>
        {contacts.length === 0 ? <div className="empty-line">No addresses yet.</div> : null}
        {contacts.map((contact) => (
          <ContactLine
            key={contact.channelIdentityId}
            contact={contact}
            canEdit={canEdit}
            confirming={confirming === contact.channelIdentityId}
            lastLive={lastLive}
            onAskRetire={() => setConfirming(contact.channelIdentityId)}
            onKeep={() => setConfirming(null)}
            onConfirmRetire={() => {
              setConfirming(null);
              onRetire(contact.channelIdentityId);
            }}
          />
        ))}
        {canEdit ? <ContactAdd user={user} onAdd={onAdd} /> : null}
      </div>
    </div>
  );
}
